import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useOutletContext } from "react-router";
import { useTranslation } from "react-i18next";
import PatientCard from "../../components/PatientCard";
import { useNavigate } from "react-router-dom";
import "./records.css";
import { ErrorModal } from "../../components/modals/ErrorModal";
import {
  appRatedAtom,
  refreshingAtom,
  isHydrated,
  recordsScrollPositionAtom,
  recordsSelectedIndexAtom,
} from "../../jotai/atoms";
import { useAtom } from "jotai";
import type { HomeContext } from "../../models/HomeContext";
import { DateTime } from "luxon";
import RefreshButton from "../../components/buttons/RefreshButton";
import FAQButton from "../../components/buttons/FAQButton";
import { Squirrel } from "../../components/spinner/Squirrel";
import { USE_WHITELABEL_BRANDING, WHITELABEL_KEY } from "../../globals";
import { getAPIClient } from "../../apiClient";
import { fetchRecordsUntilUpdated } from "../../utils/polling";
import ReactDOMServer from "react-dom/server";
import { Spinner } from "../../components/spinner/Spinner";
import useFormFactor from "../../hooks/useFormFactor";
import { Capacitor } from "@capacitor/core";
import { Dialog } from "@capacitor/dialog";
import { InAppReview } from "@capacitor-community/in-app-review";
import { initPullToRefresh } from "../../utils/pull-to-refresh";

interface RecordsTranslations {
  searchMessage: string;
  searchButton: string;
  searchError: string;
  header: string;
  subtitle: string;
  updated: string;
}

interface RecordsState {
  searchUid?: string;
}

function indexFromHash(hash: string): number | null {
  const index = parseInt(hash.substring(1));
  return isNaN(index) || index < 0 ? null : index;
}

export function Records({ recordType }: { recordType: "immunization" | "screening" }) {
  const { state } = useLocation();
  const { formFactor } = useFormFactor();
  const { searchUid } = (state as RecordsState) || {};
  const { scrollElement, allVerifiedRecords } = useOutletContext<HomeContext>();
  const [showErrorModal, setShowErrorModal] = useState<string | boolean>();
  const [scrollPosition, setScrollPosition] = useAtom(recordsScrollPositionAtom);
  const [selectedRecordIndex, setSelectedRecordIndex] = useAtom(recordsSelectedIndexAtom);

  const [cardOpen, setCardOpen] = useState<boolean[]>(() => {
    const hash = window.location.hash;
    const hashIndex = indexFromHash(hash);
    // Use hash if available, otherwise fall back to persisted selectedRecordIndex
    const index = hashIndex ?? selectedRecordIndex;
    if (index !== null) {
      const initial = Array(index + 1).fill(false);
      initial[index] = true;
      return initial;
    }
    return [];
  });

  // Sync atom state with URL hash on mount
  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      const index = indexFromHash(hash);
      if (index !== null && index !== selectedRecordIndex) {
        setSelectedRecordIndex(index);
        setScrollPosition(null);
      }
    } else if (selectedRecordIndex !== null) {
      window.history.replaceState(null, "", `#${selectedRecordIndex}`);
    }
  }, []);

  useEffect(() => {
    // Gives us the fun animation of the first card opening up when no cards are open
    if (cardOpen.length === 0) {
      setSelectedRecordIndex(0);
      setScrollPosition(0);
      setCardOpen([true]);
    }
  }, [cardOpen.length]);
  const [refreshing, setRefreshing] = useAtom(refreshingAtom);
  const [appRated, setAppRated] = useAtom(appRatedAtom);
  const [pullToRefreshMainElement, setPullToRefreshMainElement] = useState<HTMLDivElement | null>(
    null
  );
  const filteredRecords = React.useMemo(() => {
    if (allVerifiedRecords.state !== "hasData" || !isHydrated(allVerifiedRecords.data)) {
      return [];
    }

    if (recordType === "immunization") {
      return allVerifiedRecords.data;
    }

    let filteredRecords = allVerifiedRecords.data;
    // This is because for NJ health launch, we want only those test results from NJ
    if (USE_WHITELABEL_BRANDING && WHITELABEL_KEY === "nj") {
      filteredRecords = filteredRecords.filter(
        (record) => record.search.izProviderKey.toLowerCase() === "nj"
      );
    }

    // In Mobile, users can navigate to the test results view with only the relevant search displayed
    if (searchUid) {
      filteredRecords = filteredRecords.filter((r) => r.search.uid === searchUid);
    }

    return filteredRecords;
  }, [allVerifiedRecords, searchUid]);

  useEffect(() => {
    if (!scrollElement) return;
    const handleScroll = () => {
      setScrollPosition(scrollElement.scrollTop);
    };
    scrollElement.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      scrollElement.removeEventListener("scroll", handleScroll);
    };
  }, [scrollElement]);

  useEffect(() => {
    if (!scrollElement || !pullToRefreshMainElement) {
      return;
    }
    const ptr = initPullToRefresh({
      pullToRefreshMainElement,
      shouldPullToRefresh: () => {
        return scrollElement.scrollTop === 0;
      },
      onRefresh: async () => {
        // Do not await refresh so that it happens in the background without stalling the PTR
        handleRefresh();
      },
    });

    return () => ptr.destroy();
  }, [scrollElement, pullToRefreshMainElement]);

  // Restore scroll position OR scroll to hash (mutually exclusive)
  useEffect(() => {
    if (!scrollElement || filteredRecords.length === 0) {
      return;
    }

    const index = indexFromHash(window.location.hash);

    // If we have a saved position (back navigation), restore it
    if (scrollPosition !== null && (index === null || index === selectedRecordIndex)) {
      requestAnimationFrame(() => {
        scrollElement.scrollTo({ top: scrollPosition, behavior: "instant" });
      });
      return;
    }

    // Otherwise, if there's a hash, scroll to that card
    if (index !== null && index < filteredRecords.length) {
      const isMobile = window.innerWidth < 768;
      const containerSelector = isMobile
        ? '[data-testid="mobile-records-list"]'
        : '[data-testid="desktop-records-list"]';
      const container = document.querySelector(containerSelector);
      const element = container?.querySelector(`[data-card-index="${index}"]`);
      element?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [filteredRecords.length, scrollElement]); // scrollPosition intentionally omitted

  const rateTheApp = async () => {
    if (Capacitor.getPlatform() === "web" || appRated) return;

    await setAppRated(true);
    const { value } = await Dialog.confirm({
      title: "Rate Docket",
      message: "Would you like to give Docket a review?",
    });

    if (value) {
      await InAppReview.requestReview();
    }
  };

  useEffect(() => {
    // Do not want to refresh infinitely if reloading in the middle of a refresh
    setRefreshing(false);
  }, []);

  useEffect(() => {
    if (filteredRecords.length > 0) {
      if (!appRated) {
        rateTheApp();
      }
    }
  }, [JSON.stringify(filteredRecords.map((r) => r.record.uid))]);

  // Only 1 card can be open at a time
  const toggleCardOpen = (index: number) => {
    const prevState = cardOpen[index];
    // Reset the state and set scroll atoms to include the new card and current scroll position
    window.history.replaceState(null, "", `#${index}`);
    setSelectedRecordIndex(prevState ? null : index);
    setScrollPosition(scrollElement?.scrollTop ?? null);
    const newOpen = Array(filteredRecords.length).fill(false);
    newOpen[index] = !prevState;
    setCardOpen(newOpen);
  };

  const navigate = useNavigate();
  const { t } = useTranslation();

  const translation = useMemo<RecordsTranslations>(() => {
    return {
      searchMessage:
        recordType === "immunization"
          ? t("immunizations.search_records_message")
          : t("screening.search_screenings_message"),
      searchButton:
        recordType === "immunization"
          ? t("immunizations.immunizations_add_records_button")
          : t("screening.search_screenings_button"),
      searchError:
        recordType === "immunization"
          ? t("immunizations.search_records_error")
          : t("screening.search_records_error"),
      header:
        recordType === "immunization"
          ? t("immunizations.records_header")
          : t("screening.records_header"),
      subtitle:
        recordType === "immunization"
          ? formFactor === "mobile"
            ? t("immunizations.records_subtitle_mobile")
            : t("immunizations.records_subtitle")
          : formFactor === "mobile"
          ? t("screening.records_subtitle_mobile")
          : t("screening.records_subtitle"),
      updated: t("immunizations.updated"),
    };
  }, [recordType, formFactor]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await getAPIClient().refreshRecords();
    /**
     * Do not await this so that when we refresh we don't show a loading squirrel for the entire time it takes for the
     * backend to refresh all records from the IIS.
     */
    fetchRecordsUntilUpdated(true);
    setRefreshing(false);
  };

  const noPatients = () => {
    return (
      <div className="flex px-6 flex-col docket-gap-2 max-md:mt-12" data-testid="no-patients">
        <h2 className="text-black dark:text-white text-md font-black mobile-text-color mx-auto">
          {translation.searchMessage}
        </h2>
        <button
          className="btn docket-button-responsive md:w-fit max-md:w-full mx-auto"
          onClick={() => navigate("/home/search/newsearch")}
        >
          {translation.searchButton}
        </button>
      </div>
    );
  };

  const withPatients = () => {
    if (allVerifiedRecords.state !== "hasData" || !isHydrated(allVerifiedRecords.data)) {
      return null;
    }
    const lastUpdatedTime: Date | null = allVerifiedRecords.data.reduce<Date | null>(
      (maxUpdatedDate, cur) => {
        if (!cur.record.updatedAt) {
          return maxUpdatedDate || null;
        }

        const currentUpdatedDate = new Date(cur.record.updatedAt);
        if (isNaN(currentUpdatedDate.getTime())) {
          return maxUpdatedDate;
        }

        return maxUpdatedDate === null || maxUpdatedDate < currentUpdatedDate
          ? currentUpdatedDate
          : maxUpdatedDate;
      },
      null
    );

    const patientCards = (
      <div className="flex flex-col">
        {filteredRecords.map((r, index) => (
          <div data-card-index={index} key={index}>
            <PatientCard
              completeRecord={r}
              index={index}
              open={cardOpen[index] ?? false}
              onOpenToggle={() => toggleCardOpen(index)}
              screening={recordType === "screening"}
              hideHeader={!!searchUid}
            />
          </div>
        ))}
      </div>
    );
    const headerContent = (
      <div
        className="flex md:docket-gap-1 md:flex-wrap max-md:px-6"
        id="pull-to-refresh-main-records"
        ref={setPullToRefreshMainElement}
      >
        <div className="flex flex-col max-md:items-center max-md:w-full">
          {lastUpdatedTime && (
            <p className="text-dark dark:text-white md:text-lg max-md:text-sm max-md:text-dark-blue max-md:dark:text-white max-md:font-semibold">
              {translation.updated}
              <span data-testid="update-time">
                {DateTime.fromJSDate(lastUpdatedTime).toLocaleString()}
              </span>
            </p>
          )}
          <p className="pb-2 text-dark dark:text-white text-lg font-bold max-md:text-white mobile-text-color">
            {translation.header}
          </p>
        </div>
        <div className="flex docket-gap-1 ml-auto max-md:hidden">
          <RefreshButton />
          <FAQButton />
        </div>
        <div className="separater max-md:hidden"></div>
      </div>
    );

    return (
      <>
        {headerContent}
        <div data-testid="desktop-records-list" className="max-md:hidden">
          {patientCards}
        </div>
        <div data-testid="mobile-records-list" className="md:hidden">
          <div className="flex flex-col docket-gap-2">{patientCards}</div>
        </div>
      </>
    );
  };

  if (allVerifiedRecords.state === "hasError") {
    setShowErrorModal(translation.searchError);
  }

  return (
    <>
      <div className="flex-col max-md:pb-20 max-md:pt-6">
        {showErrorModal && (
          <ErrorModal onCloseModal={() => setShowErrorModal(false)}>{showErrorModal}</ErrorModal>
        )}
        {(allVerifiedRecords.state === "loading" ||
          refreshing ||
          (allVerifiedRecords.state === "hasData" && !isHydrated(allVerifiedRecords.data))) && (
          <div className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none">
            <Squirrel />
          </div>
        )}
        <div className="column is-gapless">
          {allVerifiedRecords.state === "hasData" &&
            isHydrated(allVerifiedRecords.data) &&
            allVerifiedRecords.data.length > 0 &&
            withPatients()}
          {allVerifiedRecords.state === "hasData" &&
            isHydrated(allVerifiedRecords.data) &&
            allVerifiedRecords.data.length === 0 &&
            noPatients()}
        </div>
      </div>
    </>
  );
}

export default Records;
